'use client';

import * as React from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AlertTriangle, Info, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { statusColors } from '@/lib/theme/status-colors';
import { colors } from '@/lib/theme/colors';

/**
 * Confirmation variant types
 * Determines the visual style and icon
 */
type ConfirmationVariant = 'default' | 'destructive' | 'warning' | 'info' | 'success';

/**
 * ConfirmationModal Props Interface
 */
interface ConfirmationModalProps {
  /** Extra classes for the dialog panel, e.g. a page's own skin. */
  className?: string;
  /**
   * Whether the modal is open
   */
  open: boolean;

  /**
   * Callback when open state changes
   */
  onOpenChange: (open: boolean) => void;

  /**
   * Modal title
   */
  title: string;

  /**
   * Modal description/message
   */
  description: string;

  /**
   * Visual variant
   * @default 'default'
   */
  variant?: ConfirmationVariant;

  /**
   * Confirm button label
   * @default 'Confirm'
   */
  confirmLabel?: string;

  /**
   * Cancel button label
   * @default 'Cancel'
   */
  cancelLabel?: string;

  /**
   * Callback when confirmed
   */
  onConfirm: () => void | Promise<void>;

  /**
   * Callback when cancelled (optional - defaults to just closing)
   */
  onCancel?: () => void;

  /**
   * Whether the confirm action is loading
   * Shows spinner and disables buttons
   * @default false
   */
  isLoading?: boolean;

  /**
   * Custom icon (overrides variant icon)
   */
  icon?: React.ReactNode;

  /**
   * Hide the icon completely
   * @default false
   */
  hideIcon?: boolean;

  /**
   * Additional content rendered between description and buttons
   */
  children?: React.ReactNode;

  /**
   * Prevent closing by clicking outside or ESC
   * @default false
   */
  preventClose?: boolean;

  /**
   * Whether to show the close button in top-right
   * @default true (unless preventClose is true, then defaults to false)
   */
  showCloseButton?: boolean;
}

/**
 * Get icon for variant
 */
function getVariantIcon(variant: ConfirmationVariant): React.ReactNode {
  const iconProps = { size: 20, className: 'flex-shrink-0' };

  switch (variant) {
    case 'destructive':
      return <XCircle {...iconProps} style={{ color: statusColors.error.icon }} />;
    case 'warning':
      return <AlertTriangle {...iconProps} style={{ color: statusColors.warning.icon }} />;
    case 'info':
      return <Info {...iconProps} style={{ color: statusColors.info.icon }} />;
    case 'success':
      return <CheckCircle {...iconProps} style={{ color: statusColors.success.icon }} />;
    default:
      return <Info {...iconProps} style={{ color: colors.text.secondary }} />;
  }
}

/**
 * ConfirmationModal - Reusable confirmation dialog
 *
 * A pre-built confirmation modal for common actions like delete, dismiss, etc.
 * Uses AlertDialog for proper semantic meaning and accessibility.
 *
 * Features:
 * - Multiple visual variants (default, destructive, warning, info, success)
 * - Loading state with disabled buttons
 * - Async confirm handler support
 * - Optional custom content
 * - Customizable labels
 * - Optional prevent close for critical actions
 *
 * @example Basic delete confirmation
 * ```tsx
 * const [open, setOpen] = useState(false);
 * const { mutate: deleteItem } = useDeleteItem();
 *
 * <ConfirmationModal
 *   open={open}
 *   onOpenChange={setOpen}
 *   title="Delete Item?"
 *   description="This action cannot be undone. The item will be permanently deleted."
 *   variant="destructive"
 *   confirmLabel="Delete"
 *   onConfirm={() => {
 *     deleteItem(itemId);
 *     setOpen(false);
 *   }}
 * />
 * ```
 *
 * @example With loading state
 * ```tsx
 * const [open, setOpen] = useState(false);
 * const [isDeleting, setIsDeleting] = useState(false);
 *
 * <ConfirmationModal
 *   open={open}
 *   onOpenChange={setOpen}
 *   title="Delete Audience?"
 *   description={`Are you sure you want to delete "${audience.name}"?`}
 *   variant="destructive"
 *   isLoading={isDeleting}
 *   onConfirm={async () => {
 *     setIsDeleting(true);
 *     try {
 *       await deleteAudience(audience.id);
 *       setOpen(false);
 *     } finally {
 *       setIsDeleting(false);
 *     }
 *   }}
 * />
 * ```
 *
 * @example With custom content
 * ```tsx
 * <ConfirmationModal
 *   open={open}
 *   onOpenChange={setOpen}
 *   title="Dismiss Signal?"
 *   description="Please select a reason for dismissing this signal:"
 *   variant="warning"
 *   confirmLabel="Dismiss"
 *   onConfirm={handleDismiss}
 * >
 *   <Select value={reason} onValueChange={setReason}>
 *     <SelectTrigger>
 *       <SelectValue placeholder="Select reason" />
 *     </SelectTrigger>
 *     <SelectContent>
 *       <SelectItem value="not_interested">Not Interested</SelectItem>
 *       <SelectItem value="already_engaged">Already Engaged</SelectItem>
 *     </SelectContent>
 *   </Select>
 * </ConfirmationModal>
 * ```
 */
export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  open,
  onOpenChange,
  title,
  description,
  variant = 'default',
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel,
  isLoading = false,
  icon,
  hideIcon = false,
  children,
  preventClose = false,
  showCloseButton,
  className,
}) => {
  const handleConfirm = async () => {
    await onConfirm();
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
    onOpenChange(false);
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (preventClose && !newOpen) {
      return;
    }
    onOpenChange(newOpen);
  };

  const displayIcon = hideIcon ? null : icon || getVariantIcon(variant);

  // Default showCloseButton to true, but hide it if preventClose is true (unless explicitly set)
  const effectiveShowCloseButton = showCloseButton !== undefined 
    ? showCloseButton 
    : !preventClose;

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent className={cn("sm:max-w-[425px]", className)} showCloseButton={effectiveShowCloseButton}>
        <AlertDialogHeader>
          <div className="flex items-start gap-3">
            {displayIcon && <div className="mt-0.5">{displayIcon}</div>}
            <div className="flex-1">
              <AlertDialogTitle className="text-left">{title}</AlertDialogTitle>
            </div>
          </div>
          <AlertDialogDescription className={cn('text-left', displayIcon && 'ml-8')}>
            {description}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {children && (
          <div className={cn('py-4', displayIcon && 'ml-8')}>
            {children}
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel
            onClick={handleCancel}
            disabled={isLoading}
          >
            {cancelLabel}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={isLoading}
            className={cn(
              variant === 'destructive' && 'bg-red-600 hover:bg-red-700 focus:ring-red-600'
            )}
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

